import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { cn } from "@/shared/lib/utils";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { RadioGroup, RadioGroupItem } from "../primitives/radio-group";

/** A single selectable option in the radio group. */
export type RadioOption = {
  /** Value submitted with the form. Must match the string expected by the Zod schema. */
  value: string;
  /** Text or element shown next to the radio button. */
  label: ReactNode;
  disabled?: boolean;
};

type ColocaRadioGroupFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the group. */
  label?: ReactNode;
  /** Helper text rendered below the group. Use for hints or constraints. Do not repeat the label. */
  description?: ReactNode;
  items: RadioOption[];
  /**
   * Layout direction of the options. Use `"horizontal"` for short labels in a row (e.g. Yes / No) and
   * `"vertical"` (default) for longer labels or more than three options.
   */
  orientation?: "vertical" | "horizontal";
  disabled?: boolean;
  /**
   * Marks the field as required: an asterisk on the label and `aria-required` on the group. Presentational —
   * the Zod schema still owns validation.
   */
  required?: boolean;
};

/**
 * Controlled radio group wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any single-choice selection where all options must be visible at once —
 * e.g. payment method, priority level, plan tier. Validation errors are displayed automatically via
 * `<FormMessage>`.
 *
 * For long lists or searchable selections prefer `ColocaSelectField`.
 */
export function ColocaRadioGroupField({
  name,
  label,
  description,
  items,
  orientation = "vertical",
  disabled,
  required,
}: ColocaRadioGroupFieldProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem data-disabled={disabled || undefined} required={required}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              className={orientation === "horizontal" ? "flex flex-row flex-wrap gap-x-4 gap-y-2" : undefined}
            >
              {items.map((item) => {
                const itemId = `${field.name}-${item.value}`;
                const isItemDisabled = disabled || item.disabled;
                return (
                  <div key={item.value} className="flex items-center gap-2">
                    <RadioGroupItem id={itemId} value={item.value} disabled={isItemDisabled} />
                    <label
                      htmlFor={itemId}
                      className={cn(
                        "cursor-pointer text-sm leading-none",
                        isItemDisabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      {item.label}
                    </label>
                  </div>
                );
              })}
            </RadioGroup>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
